import { unwrap } from "@sagot/api-client";
import type { DashboardRange } from "@sagot/constants";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../../../lib/query-keys";
import { useApi } from "../../../session/session-provider";
import { useWorkspaceId } from "../../../session/workspace-context";
import { wsKeys } from "../shared/query-keys";

/** `GET /v1/dashboard?range=` — KPIs, daily series, topics, languages, recent, gaps, KB health. */
export function useDashboard(range: DashboardRange, agentId: string | null) {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({
    queryKey: wsKeys.dashboard(wsId, range, agentId),
    queryFn: () => unwrap(api.GET("/v1/dashboard", { params: { query: agentId ? { range, agent_id: agentId } : { range } } })),
    placeholderData: keepPreviousData,
  });
}

/** `GET /v1/workspace/setup-checklist` — steps auto-derived from real state + dismissal flag. */
export function useSetupChecklist() {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({ queryKey: wsKeys.checklist(wsId), queryFn: () => unwrap(api.GET("/v1/workspace/setup-checklist")) });
}

/** Dismiss for everyone: `PATCH /v1/workspace {onboarding: {checklist_dismissed: true}}`. */
export function useDismissChecklist() {
  const api = useApi();
  const wsId = useWorkspaceId();
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => unwrap(api.PATCH("/v1/workspace", { body: { onboarding: { checklist_dismissed: true } } })),
    onSuccess: async () => {
      await Promise.all([client.invalidateQueries({ queryKey: wsKeys.checklist(wsId) }), client.invalidateQueries({ queryKey: queryKeys.workspace(wsId) })]);
    },
  });
}
